// TERM8-F1 (docs/build-briefs/TERM8-F1-reader-tip-settings-screen.md; DEC-335), TIPONE1 (DEC-541) and TABSPAY1
// (docs/build-briefs/TABSPAY1-tabs-and-payment-page.md). Settings > Tabs and payment > Card reader: one section with the
// tip switch, the line naming the tip choices (the same ones the tablet Pay screen uses, edited in Money and pricing), the
// cart pause, what the reader's state means, and ONE Save for the section. The location is named only as a picker, and only
// when the person manages more than one location.
//
// Over TERM8's GET and PUT /tenants/:tenantId/locations/:locationId/reader-tip-settings. The location is chosen from the
// locations the server lists (this device's location first), never typed. The section acts over its own call and is NOT part
// of the Open tabs Save. After every save it reads what the server holds and draws that, so what is on screen is what
// is stored. Visible to a session that may view settings; the fields and buttons are off for one that may not
// manage them (resolved permissions, never a role name). Admin surface: keyboard and pointer are fine, every control
// is 44px or taller. All text via textContent.

import {
  getReaderTipSettings,
  listTenantLocations,
  putReaderTipSettings,
  type ReaderTipSettingsDto,
  type TenantLocationNameDto,
} from "./api.ts";
import { sortByName } from "./nameSort.ts";
import {
  LOCATION_PICKER_LABEL,
  PAUSE_LABEL,
  PAUSE_NOTE,
  REFRESH_DELAY_LINE,
  SAVED_LINE,
  SAVED_TOAST,
  SAVE_LABEL,
  SECTION_HELP,
  SECTION_TITLE,
  SWITCH_LABEL,
  TIP_CHOICES_LINK,
  TIP_CHOICES_NOTE,
  buildPutBody,
  draftFromView,
  providerStateNote,
  providerStatePill,
  tipChoicesLine,
  type ReaderTipDraft,
} from "./readerTipsModel.ts";

export interface ReaderTipsSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.tenant_settings.view (the read of this section). */
  canView: () => boolean;
  /** The session holds admin.location_settings.manage (the save). */
  canManage: () => boolean;
  /** The location this device belongs to, when it is set up as one. */
  deviceLocation: () => { id: string; name: string } | undefined;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  /** The shared Settings toast, the same confirmation every other Settings save shows. */
  toast: (message: string, variant: "success" | "danger") => void;
  /** Opens Money and pricing at Tip presets, or undefined when this session may not open that tab. */
  openTipChoices: () => (() => void) | undefined;
}

export interface ReaderTipsSection {
  refresh(): Promise<void>;
  reset(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

const ID_PREFIX = "admin-reader-tips";
const STATUS_LINGER_MS = 6000;
const VIEW_ONLY_LINE = "View only. An owner or admin can change these.";

export function mountReaderTipsSection(
  root: HTMLElement,
  deps: ReaderTipsSectionDeps,
): ReaderTipsSection {
  let locations: { id: string; name: string }[] = [];
  let locationId: string | undefined;
  let view: ReaderTipSettingsDto | undefined;
  let draft: ReaderTipDraft | undefined;
  let statusLine = "";
  let errorLine = "";
  let saving = false;
  let loadSequence = 0;
  /** The cart-pause switch's draft; it is saved by the same Save as the tip switch. */
  let pauseDraft = false;
  let statusTimer: ReturnType<typeof setTimeout> | undefined;
  root.hidden = true;

  function clearStatusTimer(): void {
    if (statusTimer !== undefined) clearTimeout(statusTimer);
    statusTimer = undefined;
  }

  function reset(): void {
    loadSequence += 1;
    locations = [];
    locationId = undefined;
    view = undefined;
    draft = undefined;
    statusLine = "";
    errorLine = "";
    saving = false;
    pauseDraft = false;
    clearStatusTimer();
    root.replaceChildren();
    root.hidden = true;
  }

  function hide(): void {
    view = undefined;
    draft = undefined;
    root.replaceChildren();
    root.hidden = true;
  }

  async function loadLocations(): Promise<void> {
    const listed = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
    const device = deps.deviceLocation();
    if (listed.status === 200 && Array.isArray(listed.body.locations)) {
      const found = listed.body.locations as TenantLocationNameDto[];
      locations = found.map((siteLocation) => ({ id: siteLocation.id, name: siteLocation.name }));
      const preferred =
        (device !== undefined &&
          locations.find((siteLocation) => siteLocation.id === device.id)?.id) ||
        found.find((siteLocation) => siteLocation.isMain)?.id ||
        locations[0]?.id;
      locationId =
        locationId !== undefined && locations.some((siteLocation) => siteLocation.id === locationId)
          ? locationId
          : preferred;
      return;
    }
    // The list needs a right this session may not hold: the device's own location is still known.
    locations = device === undefined ? [] : [device];
    locationId = device?.id;
  }

  async function load(): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    const sequence = (loadSequence += 1);
    if (locations.length === 0) {
      await loadLocations();
      if (sequence !== loadSequence || !deps.isCurrent()) return;
    }
    if (locationId === undefined) {
      hide();
      return;
    }
    const result = await getReaderTipSettings(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status === 200 && result.body.settings !== undefined) {
      view = result.body;
      draft = draftFromView(result.body);
      pauseDraft = result.body.cartPauseEnabled === true;
      render();
      return;
    }
    if (result.status === 403 || result.status === 404) {
      hide();
      return;
    }
    view = undefined;
    draft = undefined;
    errorLine = `Could not load the card reader settings. ${messageOf(result.body)}`;
    render();
  }

  function render(): void {
    root.replaceChildren();
    root.hidden = false;
    const wrap = node("div", "rt-section tp-section");
    wrap.dataset["testid"] = `${ID_PREFIX}-body`;
    const title = node("h2", "section-title", SECTION_TITLE);
    title.id = `${ID_PREFIX}-heading`;
    // DEC-597: the section description is printed under the title.
    const sectionHelp = node("p", "admin-field-help", SECTION_HELP);
    sectionHelp.dataset["testid"] = `${ID_PREFIX}-section-help`;
    wrap.append(title, sectionHelp);

    // The reader settings are stored per location, so the picker is the scope. It shows only for a person who manages more
    // than one location; with one location there is nothing to pick and no name to print.
    if (locations.length > 1) {
      const picker = node("div", "rt-location-picker");
      picker.append(node("span", "field-label", LOCATION_PICKER_LABEL));
      const chips = node("div", "rt-locations");
      chips.setAttribute("role", "group");
      chips.setAttribute("aria-label", LOCATION_PICKER_LABEL);
      for (const siteLocation of sortByName(locations, (listed) => listed.name)) {
        const chip = node("button", "tablets-chip", siteLocation.name);
        chip.type = "button";
        chip.dataset["testid"] = `${ID_PREFIX}-location`;
        chip.dataset["locationId"] = siteLocation.id;
        chip.setAttribute("aria-pressed", String(siteLocation.id === locationId));
        chip.addEventListener("click", () => {
          if (siteLocation.id === locationId || saving) return;
          locationId = siteLocation.id;
          statusLine = "";
          errorLine = "";
          clearStatusTimer();
          void load();
        });
        chips.append(chip);
      }
      picker.append(chips);
      wrap.append(picker);
    }

    if (view === undefined || draft === undefined) {
      if (errorLine !== "") {
        const error = node("p", "admin-error", errorLine);
        error.setAttribute("role", "alert");
        error.dataset["testid"] = `${ID_PREFIX}-error`;
        wrap.append(error);
      }
      root.append(wrap);
      return;
    }

    const current = view;
    const working = draft;
    const mayManage = deps.canManage();
    const status = node("span", "admin-settings-save-status", mayManage ? statusLine : VIEW_ONLY_LINE);
    const forgetSaved = (): void => {
      statusLine = "";
      status.textContent = "";
      clearStatusTimer();
    };

    // Row 1: the tip switch, and the tip choices it offers.
    const toggleLabel = node("label", "admin-toggle-label admin-field-toggle");
    const toggle = node("input", "admin-toggle-input");
    toggle.type = "checkbox";
    toggle.id = `${ID_PREFIX}-enabled`;
    toggle.dataset["testid"] = `${ID_PREFIX}-enabled`;
    toggle.checked = working.enabled;
    toggle.disabled = !mayManage || saving;
    toggle.addEventListener("change", () => {
      working.enabled = toggle.checked;
      forgetSaved();
    });
    toggleLabel.append(
      toggle,
      node("span", "admin-toggle-track"),
      node("span", null, SWITCH_LABEL),
    );
    wrap.append(toggleLabel);

    // The one tip setting (DEC-541): named here, edited in Money and pricing (Tip presets).
    const tipRow = node("div", "rt-tip-row");
    const tipChoices = node("p", "rt-tip-choices", tipChoicesLine(current.tipChoicesBasisPoints));
    tipChoices.dataset["testid"] = `${ID_PREFIX}-tip-choices`;
    tipRow.append(tipChoices);
    const openTipChoices = deps.openTipChoices();
    if (openTipChoices !== undefined) {
      const link = node("button", "btn-secondary btn-compact", TIP_CHOICES_LINK);
      link.type = "button";
      link.dataset["testid"] = `${ID_PREFIX}-tip-choices-link`;
      link.addEventListener("click", openTipChoices);
      tipRow.append(link);
    }
    const tipChoicesNote = node("p", "admin-field-help", TIP_CHOICES_NOTE);
    tipChoicesNote.dataset["testid"] = `${ID_PREFIX}-tip-choices-note`;
    wrap.append(tipRow, tipChoicesNote);

    // Row 2: show the drinks on the reader before payment starts.
    const pauseLabel = node("label", "admin-toggle-label admin-field-toggle");
    const pauseToggle = node("input", "admin-toggle-input");
    pauseToggle.type = "checkbox";
    pauseToggle.id = `${ID_PREFIX}-cart-pause`;
    pauseToggle.dataset["testid"] = `${ID_PREFIX}-cart-pause`;
    pauseToggle.checked = pauseDraft;
    pauseToggle.disabled = !mayManage || saving;
    pauseToggle.addEventListener("change", () => {
      pauseDraft = pauseToggle.checked;
      forgetSaved();
    });
    pauseLabel.append(
      pauseToggle,
      node("span", "admin-toggle-track"),
      node("span", null, PAUSE_LABEL),
    );
    wrap.append(pauseLabel, node("p", "admin-field-help", PAUSE_NOTE));

    // Row 3: what the reader has, in a word, and one sentence saying what that means here.
    const pill = providerStatePill(current.providerState);
    const state = node("div", "rt-state");
    state.append(node("span", "field-label", "Reader status"));
    const badge = node("span", `so-badge so-badge-${pill.tone}`);
    badge.dataset["testid"] = `${ID_PREFIX}-state`;
    badge.dataset["state"] = current.providerState;
    badge.append(node("span", "so-badge-dot"), pill.label);
    const stateNote = node("span", "rt-state-note", providerStateNote(current.providerState));
    stateNote.dataset["testid"] = `${ID_PREFIX}-state-note`;
    state.append(badge, stateNote);
    wrap.append(state);

    // Save (the whole section) and the outcome.
    const actions = node("div", "admin-settings-save-row");
    const save = node("button", "btn-secondary", SAVE_LABEL);
    save.type = "button";
    save.dataset["testid"] = `${ID_PREFIX}-save`;
    save.disabled = !mayManage || saving;
    save.addEventListener("click", () => void saveSection());
    status.setAttribute("role", "status");
    status.dataset["testid"] = `${ID_PREFIX}-status`;
    actions.append(save, status);
    wrap.append(actions);
    const delay = node("p", "admin-field-help", REFRESH_DELAY_LINE);
    delay.dataset["testid"] = `${ID_PREFIX}-delay`;
    wrap.append(delay);
    if (errorLine !== "") {
      const error = node("p", "admin-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = `${ID_PREFIX}-error`;
      wrap.append(error);
    }
    root.append(wrap);
  }

  async function send(
    body: Parameters<typeof putReaderTipSettings>[3],
    savedLine: string,
  ): Promise<void> {
    if (locationId === undefined || saving) return;
    const targetLocation = locationId;
    saving = true;
    statusLine = "";
    errorLine = "";
    render();
    const result = await putReaderTipSettings(
      deps.apiBaseUrl(),
      deps.tenantId(),
      targetLocation,
      body,
    );
    if (!deps.isCurrent()) return;
    saving = false;
    if (result.status !== 200) {
      // Nothing was saved: keep what the person typed and say why.
      errorLine = messageOf(result.body);
      render();
      return;
    }
    // Read back what the server holds, so the screen shows the stored values, not the typed ones.
    const warning = result.body.syncWarning;
    await load();
    statusLine = savedLine;
    errorLine = warning ?? "";
    render();
    deps.toast(SAVED_TOAST, "success");
    // The "Saved." line belongs to this tap: it goes at the next edit, or after a few seconds.
    clearStatusTimer();
    statusTimer = setTimeout(() => {
      statusLine = "";
      const line = root.querySelector<HTMLElement>(`[data-testid="${ID_PREFIX}-status"]`);
      if (line !== null) line.textContent = "";
    }, STATUS_LINGER_MS);
  }

  /** One request carries both switches, so what Save covers is exactly the two switches on screen. */
  async function saveSection(): Promise<void> {
    if (view === undefined || draft === undefined || saving) return;
    await send({ ...buildPutBody(draft), cartPauseEnabled: pauseDraft }, SAVED_LINE);
  }

  return {
    async refresh() {
      statusLine = "";
      errorLine = "";
      await load();
    },
    reset,
  };
}
